import React, { useRef, useMemo, useEffect, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import * as THREE from 'three';

// Planet component with realistic surface
function Planet({ position = [0, 0, 0], radius = 1, color = "#1a4a1a" }) {
  const meshRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.005; // Slow planet rotation
    }
  });

  const planetGeometry = useMemo(() => new THREE.SphereGeometry(radius, 32, 32), [radius]);
  
  return (
    <mesh ref={meshRef} position={position} geometry={planetGeometry}>
      <meshStandardMaterial 
        color={color}
        emissive="#0a2a0a"
        emissiveIntensity={0.1}
        roughness={0.8}
        metalness={0.2}
      />
      {/* Planet atmosphere glow */}
      <mesh scale={1.05}>
        <sphereGeometry args={[radius, 16, 16]} />
        <meshBasicMaterial 
          color="#28f428" 
          transparent 
          opacity={0.1} 
          side={THREE.BackSide}
        />
      </mesh>
    </mesh>
  );
}

// Moon component
function Moon({ orbitRadius, speed, phase = 0, size = 0.1, color = "#666" }) {
  const meshRef = useRef();
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    const angle = time * speed + phase;
    
    if (meshRef.current) {
      meshRef.current.position.x = Math.cos(angle) * orbitRadius;
      meshRef.current.position.z = Math.sin(angle) * orbitRadius;
      meshRef.current.rotation.y += 0.02;
    }
  });

  // Create orbit path
  const orbitPoints = useMemo(() => {
    const points = [];
    for (let i = 0; i <= 64; i++) {
      const angle = (i / 64) * Math.PI * 2;
      points.push(new THREE.Vector3(
        Math.cos(angle) * orbitRadius,
        0,
        Math.sin(angle) * orbitRadius
      ));
    }
    return points;
  }, [orbitRadius]);

  return (
    <group>
      {/* Orbit path */}
      <Line
        points={orbitPoints}
        color="#28f428"
        opacity={0.2}
        transparent
        lineWidth={1}
      />
      {/* Moon */}
      <mesh ref={meshRef}>
        <sphereGeometry args={[size, 16, 16]} />
        <meshStandardMaterial 
          color={color}
          emissive="#0a1a0a"
          emissiveIntensity={0.05}
        />
      </mesh>
    </group>
  );
}

// Satellite component with realistic design
function Satellite({ orbitRadius, speed, phase = 0 }) {
  const groupRef = useRef();
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    const angle = time * speed + phase;
    const x = Math.cos(angle) * orbitRadius;
    const z = Math.sin(angle) * orbitRadius;
    const y = Math.sin(time * 0.5) * 0.1; // Slight orbital inclination
    
    if (groupRef.current) {
      groupRef.current.position.set(x, y, z);
      groupRef.current.lookAt(0, 0, 0); // Always face the planet
    }
  });

  // Create satellite orbit path
  const orbitPoints = useMemo(() => {
    const points = [];
    for (let i = 0; i <= 64; i++) {
      const angle = (i / 64) * Math.PI * 2;
      points.push(new THREE.Vector3(
        Math.cos(angle) * orbitRadius,
        Math.sin(i * 0.1) * 0.1,
        Math.sin(angle) * orbitRadius
      ));
    }
    return points;
  }, [orbitRadius]);

  return (
    <group>
      {/* Satellite orbit path */}
      <Line
        points={orbitPoints}
        color="#28f428"
        opacity={0.4}
        transparent
        lineWidth={2}
        dashed
        dashSize={0.1}
        gapSize={0.05}
      />
      
      {/* Satellite */}
      <group ref={groupRef}>
        {/* Main body */}
        <mesh>
          <boxGeometry args={[0.08, 0.06, 0.12]} />
          <meshStandardMaterial 
            color="#333" 
            emissive="#28f428"
            emissiveIntensity={0.1}
            metalness={0.8}
            roughness={0.2}
          />
        </mesh>
        
        {/* Solar panels */}
        <mesh position={[-0.1, 0, 0]}>
          <boxGeometry args={[0.02, 0.12, 0.08]} />
          <meshStandardMaterial 
            color="#1a1a3a" 
            emissive="#28f428"
            emissiveIntensity={0.05}
          />
        </mesh>
        <mesh position={[0.1, 0, 0]}>
          <boxGeometry args={[0.02, 0.12, 0.08]} />
          <meshStandardMaterial 
            color="#1a1a3a" 
            emissive="#28f428"
            emissiveIntensity={0.05}
          />
        </mesh>
        
        {/* Communication dish */}
        <mesh position={[0, 0.05, 0]} rotation={[0, 0, 0]}>
          <cylinderGeometry args={[0.03, 0.03, 0.01, 16]} />
          <meshStandardMaterial 
            color="#666" 
            emissive="#28f428"
            emissiveIntensity={0.2}
            metalness={0.9}
          />
        </mesh>
        
        {/* Signal beam */}
        <mesh position={[0, -0.5, 0]} rotation={[0, 0, 0]}>
          <cylinderGeometry args={[0.3, 0.01, 1, 8, 1, true]} />
          <meshBasicMaterial 
            color="#28f428" 
            transparent 
            opacity={0.1}
            side={THREE.DoubleSide}
          />
        </mesh>
      </group>
    </group>
  );
}

// Starfield background
function Starfield() {
  const starsRef = useRef();
  
  const starPositions = useMemo(() => {
    const positions = new Float32Array(1000 * 3);
    for (let i = 0; i < 1000; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 100;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 100;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 100;
    }
    return positions;
  }, []);

  useFrame(() => {
    if (starsRef.current) {
      starsRef.current.rotation.y += 0.0002;
    }
  });

  return (
    <points ref={starsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={1000}
          array={starPositions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial 
        color="#28f428" 
        size={0.02} 
        transparent 
        opacity={0.6}
      />
    </points>
  );
}

// Camera controller for cinematic movement with responsive positioning
function CameraController() {
  const { camera, size } = useThree();
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    // Responsive camera distance based on screen size
    const isMobile = size.width < 600;
    const isTablet = size.width < 900;
    
    let baseRadius = 8;
    let heightVariation = 1;
    
    if (isMobile) {
      baseRadius = 12; // Further back on mobile for better view
      heightVariation = 0.5;
    } else if (isTablet) {
      baseRadius = 10;
      heightVariation = 0.8;
    }
    
    // Smooth orbital camera movement
    const radius = baseRadius + Math.sin(time * 0.1) * 2;
    const height = 3 + Math.sin(time * 0.15) * heightVariation;
    
    camera.position.x = Math.cos(time * 0.05) * radius;
    camera.position.y = height;
    camera.position.z = Math.sin(time * 0.05) * radius;
    
    camera.lookAt(0, 0, 0);
    
    // Adjust FOV for mobile
    if (isMobile && camera.fov !== 55) {
      camera.fov = 55;
      camera.updateProjectionMatrix();
    } else if (!isMobile && camera.fov !== 45) {
      camera.fov = 45;
      camera.updateProjectionMatrix();
    }
  });
  
  return null;
}

// Main 3D Scene
function OrbitalScene() {
  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.1} color="#28f428" />
      <directionalLight 
        position={[10, 10, 5]} 
        intensity={0.5} 
        color="#ffffff"
        castShadow
      />
      <pointLight 
        position={[0, 0, 0]} 
        intensity={0.3} 
        color="#28f428"
        distance={20}
      />
      
      {/* Scene objects */}
      <Starfield />
      <Planet radius={1.2} />
      
      {/* Multiple moons */}
      <Moon orbitRadius={2.5} speed={0.3} phase={0} size={0.15} color="#888" />
      <Moon orbitRadius={3.8} speed={0.2} phase={Math.PI} size={0.12} color="#666" />
      <Moon orbitRadius={5.2} speed={0.15} phase={Math.PI * 0.5} size={0.08} color="#555" />
      
      {/* Target satellite */}
      <Satellite orbitRadius={6.5} speed={0.12} phase={Math.PI * 1.5} />
      
      {/* Camera controller */}
      <CameraController />
    </>
  );
}

// Enhanced tracking data overlay with more flair
function TrackingOverlay() {
  const [data, setData] = useState({
    altitude: 0,
    velocity: 0,
    signal: 0,
    distance: 0,
    status: 'TRACKING',
    lock: true
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const time = Date.now();
      const isLocked = Math.sin(time * 0.001) > -0.8; // Occasional signal loss
      
      setData({
        altitude: (420 + Math.sin(time * 0.001) * 15).toFixed(1),
        velocity: (7.66 + Math.sin(time * 0.002) * 0.1).toFixed(2),
        signal: isLocked ? (85 + Math.sin(time * 0.003) * 10).toFixed(0) : (45 + Math.random() * 20).toFixed(0),
        distance: (1247 + Math.sin(time * 0.0015) * 50).toFixed(0),
        status: isLocked ? 'LOCKED' : 'ACQUIRING',
        lock: isLocked
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="tracking-overlay">
      {/* Enhanced tracking data */}
      <div className="tracking-data">
        <div className="data-row">
          <span className="label">ALT:</span>
          <span className="value">{data.altitude} km</span>
        </div>
        <div className="data-row">
          <span className="label">VEL:</span>
          <span className="value">{data.velocity} km/s</span>
        </div>
        <div className="data-row">
          <span className="label">SIG:</span>
          <span className={`value ${data.lock ? 'signal-strong' : 'signal-weak'}`}>
            {data.signal}%
          </span>
        </div>
        <div className="data-row">
          <span className="label">RNG:</span>
          <span className="value">{data.distance} km</span>
        </div>
        <div className="data-row status-row">
          <span className="label">STS:</span>
          <span className={`value status-indicator ${data.lock ? 'status-locked' : 'status-acquiring'}`}>
            {data.status}
          </span>
        </div>
      </div>
      
      {/* Simple crosshair */}
      <div className="crosshair">
        <div className="crosshair-h"></div>
        <div className="crosshair-v"></div>
      </div>
      
      {/* Signal pulse indicators */}
      <div className="signal-pulses">
        <div className="pulse pulse-1"></div>
        <div className="pulse pulse-2"></div>
        <div className="pulse pulse-3"></div>
      </div>
    </div>
  );
}

// Main component with responsive optimization
export default function OrbitalVisualization() {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 600);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="orbital-container">
      <Canvas
        camera={{ position: [8, 3, 8], fov: 45 }}
        gl={{ 
          antialias: !isMobile, // Disable antialiasing on mobile for performance
          alpha: true,
          powerPreference: "high-performance",
          pixelRatio: Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2) // Limit pixel ratio on mobile
        }}
        dpr={isMobile ? [1, 1.5] : [1, 2]} // Lower DPR on mobile
        performance={{ min: 0.5 }} // Allow frame rate to drop for performance
      >
        <OrbitalScene />
      </Canvas>
      <TrackingOverlay />
    </div>
  );
}
